import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { PhoneFilters, PhoneBookEntry, ShivarOption } from '../types';
import { useContacts } from '../hooks/useContacts';
import { useAuth } from '../contexts/AuthContext';
import { FilterBar } from '../components/FilterBar';
import { PhoneBookTable } from '../components/PhoneBookTable';
import { PhoneBookCard } from '../components/PhoneBookCard';
import ElectricBorder from '../components/ElectricBorder';
import StarBorder from '../components/StarBorder';
import { Loading } from '../components/ui/Loading';
import { Empty } from '../components/ui/Empty';
import { ErrorAlert } from '../components/ui/ErrorAlert';
import { formatShivar } from '../utils/shivar';

export const PhoneBook = () => {
  const [filters, setFilters] = useState<PhoneFilters>({});
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState({ shivars: [] as ShivarOption[] });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  const fetchOptions = async () => {
    setOptionsLoading(true);
    setOptionsError(null);
    try {
      const { data, error: dbError } = await supabase.from('shivars').select('name, marathi_name').order('name');
      if (dbError) throw dbError;
      setOptions({
        shivars: (data ?? []).map((s) => ({ value: s.name, label: formatShivar(s.name, s.marathi_name) })),
      });
    } catch (err) {
      setOptionsError(err instanceof Error ? err.message : 'Failed to load shivars');
    } finally {
      setOptionsLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  const { contacts, count, loading, error, pageSize } = useContacts({ ...filters, page });

  const totalPages = Math.ceil(count / pageSize);
  const shivarMap = Object.fromEntries(options.shivars.map((s) => [s.value, s.label]));
  const { role } = useAuth();

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
        <ElectricBorder
          color="#f97316"
          speed={1}
          chaos={0.05}
          thickness={1}
          borderRadius={9999}
          style={{ borderRadius: '50%', display: 'inline-block' }}
        >
          <img
            src="/static/logo.png"
            alt="Tandulwadi Phone book"
            className="mx-auto h-24 w-24 object-contain"
          />
        </ElectricBorder>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <StarBorder
            as="a"
            href="/static/logo.png"
            download="tandaulwadi-logo.png"
            color="#60a5fa"
            backgroundColor="#2563eb"
            borderColor="#2563eb"
            textColor="#ffffff"
            speed="6s"
            thickness={1}
          >
            Download Logo
          </StarBorder>
          <StarBorder
            as={Link}
            to={role === 'EMPLOYEE' ? '/employee/add-number' : '/add-contact'}
            color="#4ade80"
            backgroundColor="#16a34a"
            borderColor="#16a34a"
            textColor="#ffffff"
            speed="6s"
            thickness={1}
          >
            Add Contact Request
          </StarBorder>
        </div>
        <h1 className="mt-4 text-2xl font-bold text-gray-900">Tandulwadi Phone book</h1>
      </section>
      {optionsError && <ErrorAlert message={optionsError} onRetry={fetchOptions} />}
      {optionsLoading ? <Loading /> : <FilterBar filters={filters} onChange={(f) => { setFilters(f); setPage(1); }} options={options} />}
      {error && <ErrorAlert message={error} onRetry={() => window.location.reload()} />}
      {loading ? (
        <Loading />
      ) : contacts.length === 0 ? (
        <Empty title="No contacts found" message="Try adjusting your search or filters." />
      ) : (
        <>
          <div className="hidden md:block">
            <PhoneBookTable contacts={contacts} shivarMap={shivarMap} showEdit={false} />
          </div>
          <div className="grid grid-cols-1 gap-4 md:hidden">
            {contacts.map((contact: PhoneBookEntry) => (
              <PhoneBookCard key={contact.id} contact={contact} shivarMap={shivarMap} showEdit={false} />
            ))}
          </div>
        </>
      )}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 pt-4">
          <button
            onClick={() => setPage((p: number) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-2 py-1 text-sm text-gray-700">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p: number) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
      <footer className="mt-8 border-t border-gray-200 py-4 text-center text-sm text-gray-600">
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact-us" className="hover:text-blue-700">
            Contact Us
          </Link>
          <Link to="/privacy-policy" className="hover:text-blue-700">
            Privacy Policy
          </Link>
        </div>
        <p className="mt-2">Tandulwadi Phone book — All Rights Reserved by TK</p>
      </footer>
    </div>
  );
};
